import moment from 'moment'
import { useState } from 'react'
import ReactApexChart from 'react-apexcharts'

import { tooltipForConsuptionChart } from 'components/group/utils/tooltipsForCharts'
import { COLORS } from 'styles/colors'
import { localizationApexCharts } from 'utils'
import '../../styles/module.ApexChartTooltip.css'
import { getDynamicColumnWidth } from '../helper/getDynamicColumnWidth'

const GroupApexConsuption = props => {
	const fetchData = props.fetchData ?? []
	const [cursorPosition, setCursorPosition] = useState(null)
	const handleMouseMove = e => {
		if (props.daysBetweenDates === 1) {
			const chartElement = document.getElementById('consuptionChart')
			if (chartElement) {
				const chartRect = chartElement.getBoundingClientRect()
				const mouseX = e.clientX - chartRect.left
				const chartWidth = chartRect.width

				if (mouseX <= chartWidth / 2) {
					setCursorPosition('left')
				} else {
					setCursorPosition('right')
				}
			}
		}
	}
	const psvValuesForTooltip = fetchData.map(el => (el.psv ? el.psv : 0))
	const headQtyValuesForTooltip = fetchData.map(el =>
		el.cattle_count ? el.cattle_count : 0,
	)
	const distributedValuesForTooltip = fetchData.map(el => el.distributed_sum)
	const cleanedValuesForTooltip = fetchData.map(el => el.cleaned_sum)
	const distributedValuesForRenderInChart = fetchData.map(el => {
		if (el.distributed_sum !== null && el.cleaned_sum !== null) {
			return el.distributed_sum > el.cleaned_sum
				? el.distributed_sum - el.cleaned_sum
				: el.distributed_sum
		} else {
			return el.distributed_sum
		}
	})

	const cleanedValuesForRenderInChart = fetchData.map(el => {
		if (el.distributed_sum !== null && el.cleaned_sum !== null) {
			return el.cleaned_sum > el.distributed_sum
				? el.cleaned_sum - el.distributed_sum
				: el.cleaned_sum
		} else {
			return el.cleaned_sum
		}
	})
	const dataXasis = fetchData.map(el => el.date)
	let maxSeriesValue = -Infinity

	for (const data of fetchData) {
		const distributedSum =
			data.distributed_sum !== null ? data.distributed_sum : 0
		const cleanedSum = data.cleaned_sum !== null ? data.cleaned_sum : 0
		const sum =
			distributedSum > cleanedSum
				? Math.round(distributedSum)
				: Math.round(cleanedSum)

		if (sum > maxSeriesValue) {
			maxSeriesValue = sum
		}
	}

	const yearsValues = []
	const sortedData = [...fetchData].sort(
		(a, b) => moment(a.date).valueOf() - moment(b.date).valueOf(),
	)
	if (sortedData.length === 0) {
		return null
	}
	// Берем первую и последнюю дату из данных
	const firstDate = moment(sortedData[0].date).startOf('month')
	const lastDate = moment(sortedData[sortedData.length - 1].date).endOf('month')
	// Вычисляем количество месяцев в диапазоне
	const monthsCount = lastDate.diff(firstDate, 'months') + 1

	for (let i = 0; i < monthsCount; i++) {
		const startDate = firstDate.clone().add(i, 'months').startOf('month')
		const endDate = firstDate.clone().add(i, 'months').endOf('month')
		const filteredData = sortedData.filter(item =>
			moment(item.date).isBetween(startDate, endDate, null, '[]'),
		)
		const distributed_sum = filteredData.reduce(
			(sum, item) => sum + (item.distributed_sum || 0),
			0,
		)
		const cleaned_sum = filteredData.reduce(
			(sum, item) => sum + (item.cleaned_sum || 0),
			0,
		)
		const averagePSV =
			filteredData.reduce((sum, item) => sum + (item.psv || 0), 0) /
			filteredData.length
		const averageHeadQty =
			filteredData.reduce((sum, item) => sum + (item.cattle_count || 0), 0) /
			filteredData.length
		const monthData = {
			date: startDate.valueOf(),
			distributed_sum,
			cleaned_sum,
			averagePSV,
			averageHeadQty,
		}
		yearsValues.push(monthData)
	}
	yearsValues.sort((a, b) => a.date - b.date)

	const yearsDistributedValues = yearsValues?.map(el => {
		if (el.distributed_sum !== null && el.cleaned_sum !== null) {
			return el.distributed_sum > el.cleaned_sum
				? el.distributed_sum - el.cleaned_sum
				: el.distributed_sum
		} else {
			return el.distributed_sum
		}
	})
	const yearsCleanedValues = yearsValues?.map(el => {
		if (el.distributed_sum !== null && el.cleaned_sum !== null) {
			return el.cleaned_sum > el.distributed_sum
				? el.cleaned_sum - el.distributed_sum
				: el.cleaned_sum
		} else {
			return el.cleaned_sum
		}
	})
	const yearsAveragePSV = yearsValues?.map(el => el.averagePSV)
	const yearsAverageHeadQty = yearsValues?.map(el => el.averageHeadQty)
	const dataXYearsValues = yearsValues?.map(el => new Date(el.date).getTime())

	let maxYearsSeriesValue = -Infinity

	for (const data of yearsValues) {
		const distributedSum =
			data.distributed_sum !== null ? data.distributed_sum : 0
		const cleanedSum = data.cleaned_sum !== null ? data.cleaned_sum : 0

		const sum =
			distributedSum > cleanedSum
				? Math.round(distributedSum)
				: Math.round(cleanedSum)

		if (sum > maxYearsSeriesValue) {
			maxYearsSeriesValue = sum
		}
	}

	const chartCategories =
		props.daysBetweenDates === 2 ? ['', ...dataXasis, ''] : dataXasis
	const distributedValuesWithPadding =
		props.daysBetweenDates === 2
			? [null, ...distributedValuesForRenderInChart, null]
			: distributedValuesForRenderInChart

	const cleanedValuesWithPadding =
		props.daysBetweenDates === 2
			? [null, ...cleanedValuesForRenderInChart, null]
			: cleanedValuesForRenderInChart

	const series = [
		{
			name: 'Остаток',
			color: COLORS.error,
			data:
				props.daysBetweenDates > 90
					? yearsCleanedValues
					: cleanedValuesWithPadding,
		},
		{
			name: 'Роздано корма',
			color: COLORS.main,
			data:
				props.daysBetweenDates > 90
					? yearsDistributedValues
					: distributedValuesWithPadding,
		},
	]

	if (
		distributedValuesForTooltip.some(value => value === null) &&
		cleanedValuesForTooltip.some(value => value === null) &&
		props.daysBetweenDates < 90
	) {
		series.unshift({
			name: 'Нет данных',
			color: COLORS.inputBorder,
			data: fetchData.map(el => {
				if (el.distributed_sum === null && el.cleaned_sum === null) {
					return maxSeriesValue
				}
				return 0
			}),
		})
	}

	const options: any = {
		colors: [COLORS.main, COLORS.inputBorder, COLORS.error],
		mask: false,
		chart: {
			animations: {
				enabled: false,
			},
			zoom: {
				enabled: false,
			},
			stacked: true,
			locales: localizationApexCharts(),
			defaultLocale: 'ru',

			height: 250,
			type: 'bar',
			toolbar: {
				show: false,
			},
		},
		title: {
			style: {
				fontSize: '12px',
				fontFamily: 'Montserrat',
				fontStyle: 'normal',
				fontWeight: 400,
				lineHeight: '15px',
				color: '#7F7F84',
			},
			text: 'Вес, кг',
		},

		dataLabels: {
			enabled: false,
		},
		grid: {
			padding: {
				left: 0,
				right: 0,
			},
			strokeDashArray: [6, 3],
			show: true,
			position: 'back',
			xaxis: {
				lines: {
					show: true,
				},
			},
		},

		plotOptions: {
			bar: {
				horizontal: false,
				borderRadius:
					props.daysBetweenDates <= 1
						? 20
						: props.daysBetweenDates <= 60
							? 8
							: props.daysBetweenDates <= 91
								? 4
								: 8,
				borderRadiusApplication: 'end',
				borderRadiusWhenStacked: 'last',
				columnWidth: getDynamicColumnWidth(
					props.daysBetweenDates > 90 ? dataXYearsValues : chartCategories,
				),

				dataLabels: {
					hideOverflowingLabels: true,
					total: {
						offsetX: 200,
						enabled: false,
						style: {
							fontSize: '13px',
							fontWeight: 900,
						},
					},
				},
			},
		},
		fill: {
			opacity: 1,
		},
		xaxis: {
			crosshairs: {
				show: false,
			},
			type: 'category',
			tickPlacement: 'between',
			tickAmount: 31,
			tooltip: {
				enabled: false,
			},
			labels: {
				rotate: 0,
				offsetX: props.daysBetweenDates === 2 ? 50 : 0,
				trim: false,
				hideOverlappingLabels: false,
				formatter: function (value: string) {
					if (!value) {
						return ''
					}

					return moment(value).format('DD.MM')
				},

				datetimeUTC: false,
				style: {
					fontSize: '12px',
					fontFamily: 'Montserrat',
					fontStyle: 'normal',
					fontWeight: 400,
					lineHeight: '15px',
					color: '#7F7F84',
				},
			},
			categories:
				props.daysBetweenDates > 90 ? dataXYearsValues : chartCategories,
		},
		yaxis: {
			crosshairs: {
				show: false,
			},
			tickAmount: 4,
			min: 0,
			max:
				props.daysBetweenDates > 90
					? maxYearsSeriesValue + 1
					: maxSeriesValue + 1,
			labels: {
				style: {
					fontSize: '12px',
					fontFamily: 'Montserrat',
					fontStyle: 'normal',
					fontWeight: 400,
					lineHeight: '15px',
					color: '#7F7F84',
				},
				formatter: function (value: number, index: number) {
					if (index % 2 !== 0) {
						return ''
					}
					return Math.round(value).toLocaleString().toString()
				},
			},
		},
		states: {
			normal: {
				filter: {
					type: 'none',
					value: 0,
				},
			},
			hover: {
				filter: {
					type: 'none',
				},
			},
			active: {
				allowMultipleDataPointsSelection: false,
				filter: {
					type: 'none',
					value: 0,
				},
			},
		},
		tooltip: {
			custom: ({ series, dataPointIndex }) => {
				return tooltipForConsuptionChart(
					series,
					dataPointIndex,
					props.daysBetweenDates,
					distributedValuesForTooltip,
					cleanedValuesForTooltip,
					psvValuesForTooltip,
					headQtyValuesForTooltip,
					yearsDistributedValues,
					yearsCleanedValues,
					yearsAveragePSV,
					yearsAverageHeadQty,
					dataXasis,
				)
			},
		},
		legend: {
			show: false,
		},
	}
	return (
		<div id={'#chart'}>
			<div
				style={{ marginLeft: '-8px' }}
				id={'consuptionChart'}
				onMouseMove={handleMouseMove}
			>
				<ReactApexChart
					key={`chart-${props.daysBetweenDates}`}
					id={'apex-chart'}
					options={options}
					series={series}
					type='bar'
					height={props.height ?? 250}
					className={'apex-chart'}
				/>
			</div>
		</div>
	)
}

export default GroupApexConsuption
